import React, { createContext, useContext, useState, useEffect } from 'react';

// Create context
const AuthContext = createContext();

// Custom hook to use the auth context
export const useAuth = () => useContext(AuthContext);

// Default admin credentials (in a real app, this would be handled by a backend)
const DEFAULT_ADMIN = {
  username: 'admin',
  password: 'NMD@2792'
};

// Storage key that works across domains
const AUTH_STORAGE_KEY = 'nmdiamond_auth';

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check if user is already logged in on component mount
  useEffect(() => {
    const checkAuth = () => {
      try {
        // Try to get auth from sessionStorage (works better across domains than localStorage)
        const storedUser = sessionStorage.getItem(AUTH_STORAGE_KEY) || localStorage.getItem(AUTH_STORAGE_KEY);
        
        if (storedUser) {
          try {
            const parsedUser = JSON.parse(storedUser);
            setUser(parsedUser);
            setIsAuthenticated(true);
            
            // Ensure it's in both storage types
            sessionStorage.setItem(AUTH_STORAGE_KEY, storedUser);
            localStorage.setItem(AUTH_STORAGE_KEY, storedUser);
          } catch (error) {
            console.error('Error parsing user from storage:', error);
            sessionStorage.removeItem(AUTH_STORAGE_KEY);
            localStorage.removeItem(AUTH_STORAGE_KEY);
          }
        }
      } catch (error) {
        console.error('Error accessing storage:', error);
      }
      
      setLoading(false);
    };

    checkAuth();
  }, []);

  // Login function
  const login = (username, password) => {
    // In a real app, this would make an API call to verify credentials
    if (username === DEFAULT_ADMIN.username && password === DEFAULT_ADMIN.password) {
      const userData = { username, role: 'admin' };
      setUser(userData);
      setIsAuthenticated(true);
      
      try {
        // Store in both sessionStorage and localStorage
        const userDataString = JSON.stringify(userData);
        sessionStorage.setItem(AUTH_STORAGE_KEY, userDataString);
        localStorage.setItem(AUTH_STORAGE_KEY, userDataString);
      } catch (error) {
        console.error('Error storing authentication:', error);
      }
      
      return true;
    }
    return false;
  };

  // Logout function
  const logout = () => {
    setUser(null);
    setIsAuthenticated(false);
    
    try {
      sessionStorage.removeItem(AUTH_STORAGE_KEY);
      localStorage.removeItem(AUTH_STORAGE_KEY);
    } catch (error) {
      console.error('Error removing authentication:', error);
    }
  };

  // Context value
  const value = {
    isAuthenticated,
    user,
    loading,
    login,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext; 